import { makeAutoObservable, runInAction } from 'mobx';
import { justifications, OKResponse, statuses } from '@/API';
import type { OrderForWorklist } from '@/Models/GenericWorklist/WorklistResult';
import * as API from '@/API';
import { ErrorResponse } from '@/Stores/UserDefaultsStore';

export interface JustificationRemark {
    ID: number;
    Name: string;
    Remark: string;
    Active: boolean;
    ForProcedures: string[] | null;
}

export interface JustificationProcedure {
    OrderProcedure_UID: number;
    ProcedureColor: string;
    ProcedureModality: string;
    ProcedureCode: string;
    ProcedureFullCode: string;
    ProcedureDescription: string;
    Remarks: JustificationRemark[];
    Justification: string;
    JustificationUsername: string | null;
    JustificationDatetimeString: string | null;
    Required: boolean;
    ChangeToStatus: number;
}

export interface JustificationProcedureState extends JustificationProcedure {
    selectedJustification?: string | null;
    changeToStatus: number;
    required: boolean;
    includedDisabledStatusIds: number[];
}

export interface StatusOption {
    value: number;
    label: string;
    disabled?: boolean;
}

export const PLACEHOLDER_JUSTIFICATION_VALUE = '-1';

export default class JustificationPopupStore {
    procedures: JustificationProcedureState[] = [];

    isLoading = true;
    isSaving = false;
    statusOptions: StatusOption[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    loadProcedures = async (order: OrderForWorklist): Promise<void> => {
        this.isLoading = true;
        const [response, statusList] = await Promise.all([
            justifications.getProceduresForJustification(order.OrderInfo.OrderID, order.PatientInfo.ID),
            statuses.getJustificationDropdown(),
        ]);

        runInAction(() => {
            this.statusOptions = statusList.map((s) => ({
                value: s.Id,
                label: s.Name,
                disabled: s.Disabled,
            }));

            const disabledStatusIds = new Set(
                this.statusOptions.filter((o) => o.disabled).map((o) => o.value),
            );

            if (response.AllOk && response.List.length > 0) {
                this.procedures = response.List.map((proc) => ({
                    ...proc,
                    selectedJustification: PLACEHOLDER_JUSTIFICATION_VALUE,
                    changeToStatus: proc.ChangeToStatus,
                    required: proc.Required,
                    includedDisabledStatusIds: disabledStatusIds.has(proc.ChangeToStatus)
                        ? [proc.ChangeToStatus]
                        : [],
                }));
            }
            else {
                this.procedures = [];
            }

            this.isLoading = false;
        });
    };

    setJustificationRemark = (procedureIndex: number, value: string): void => {
        if (value === PLACEHOLDER_JUSTIFICATION_VALUE || value == null) {
            return;
        }
        const id = Number.parseInt(value, 10);
        if (Number.isNaN(id)) {
            return;
        }
        const procedure = this.procedures[procedureIndex];
        const selectedRemark = procedure.Remarks.find((r) => r.ID === id);

        if (!selectedRemark) {
            return;
        }
        const currentJustification = (procedure.Justification ?? '').trim();
        const newJustification = currentJustification === ''
            ? selectedRemark.Remark
            : `${currentJustification} ${selectedRemark.Remark}`;

        const updatedProcedures = [...this.procedures];
        updatedProcedures[procedureIndex] = {
            ...procedure,
            Justification: newJustification,
            selectedJustification: PLACEHOLDER_JUSTIFICATION_VALUE,
        };
        this.procedures = updatedProcedures;
    };

    setJustificationText = (procedureIndex: number, value: string): void => {
        const updatedProcedures = [...this.procedures];
        updatedProcedures[procedureIndex] = { ...updatedProcedures[procedureIndex], Justification: value };
        this.procedures = updatedProcedures;
    };

    setStatus = (procedureIndex: number, value: number): void => {
        const procedure = this.procedures[procedureIndex];
        const previous = procedure.changeToStatus;
        const prevOpt = this.statusOptions.find((o) => o.value === previous);
        const previousWasDisabled = prevOpt?.disabled === true;
        const alreadyIncluded = procedure.includedDisabledStatusIds.includes(previous);
        const added = previousWasDisabled && !alreadyIncluded
            ? [...procedure.includedDisabledStatusIds, previous]
            : procedure.includedDisabledStatusIds;
        const updatedProcedures = [...this.procedures];
        updatedProcedures[procedureIndex] = {
            ...procedure,
            changeToStatus: value,
            includedDisabledStatusIds: added,
        };
        this.procedures = updatedProcedures;
    };

    setRequired = (procedureIndex: number, checked: boolean): void => {
        const updatedProcedures = [...this.procedures];
        updatedProcedures[procedureIndex] = { ...updatedProcedures[procedureIndex], required: checked };
        this.procedures = updatedProcedures;
    };

    save = async (setJustificationModalNotVisible: () => void, refreshWorklist: () => Promise<void>): Promise<void> => {
        this.isSaving = true;

        const justificationModels = this.procedures.map((proc) => ({
            OrderProcedure_UID: proc.OrderProcedure_UID,
            Justification: proc.Justification,
            ChangeToStatus: proc.changeToStatus,
            Required: proc.required,
        }));

        await API.justifications.saveJustifications<OKResponse, ErrorResponse>(
            justificationModels,
            () => {
                void (async () => {
                    setJustificationModalNotVisible();
                    await refreshWorklist();
                })();
            },
            (res: ErrorResponse) => {
                console.error('Failed to save justifications', res);
            },
        );

        runInAction(() => {
            this.isSaving = false;
        });
    };

    getAvailableStatusOptions = (procedureIndex: number): StatusOption[] => {
        const procedure = this.procedures[procedureIndex];
        return this.statusOptions.filter(
            (opt) => !opt.disabled
                || opt.value === procedure.changeToStatus
                || procedure.includedDisabledStatusIds.includes(opt.value),
        );
    };

    getPatientDisplayName = (order: OrderForWorklist): string => {
        const firstName = order.PatientInfo.FirstName ?? '';
        const lastName = order.PatientInfo.LastName;
        if (firstName === '') {
            return lastName;
        }
        return `${firstName} ${lastName}`;
    };
}
